import type { Action, PlayerView, Plan } from '@il-colpo/engine';
import type { GameConnection } from '@il-colpo/protocol';
import type { GameClient, GameMode, SendResult } from '../game/client.ts';

/**
 * The heist as the game screens see it, when the server runs it: the latest view the server sent, and
 * the actions of the player passed on to it. The rules decide on the server; a refused action comes
 * back as an error message (shown as a toast by `useOnlineErrors`), not as the result of `send`.
 *
 * Nothing here pauses (GDD §11: not online), nothing is taught, and the bots are not this client's.
 */
export class OnlineGameClient implements GameClient {
  readonly mode: GameMode = 'online';
  readonly chapter = null;
  readonly canPause = false;
  private last: PlayerView;

  constructor(private readonly connection: GameConnection) {
    const view = connection.getState().view;
    if (!view) throw new Error('an online game needs a view to start from');
    this.last = view;
  }

  get humanId(): string {
    return this.connection.getState().you ?? this.last.you.id;
  }

  lessonDone(): boolean {
    return false;
  }

  view(): PlayerView {
    const view = this.connection.getState().view;
    if (view) this.last = view;
    return this.last;
  }

  version(): number {
    return this.connection.version();
  }

  subscribe(listener: () => void): () => void {
    return this.connection.subscribe(listener);
  }

  send(action: Action): SendResult {
    this.connection.act(action);
    return { ok: true };
  }

  paused(): boolean {
    return false;
  }

  setPaused(): void {
    // No pause online.
  }

  suggestPlan(): Plan | null {
    return null;
  }

  autopilot(): boolean {
    return false;
  }

  /** Leaving the heist: the seat goes to a bot for good. */
  dispose(): void {
    void this.connection.leave();
  }
}
